import { LomavisList1 } from '@/lomavisSharedUi/atoms/lists/LomavisList1';
import React, { useEffect, useMemo, useState } from 'react';

import { useTranslate } from '@tolgee/react';
import PlaceholderImage from '../../assets/images/placeholders/images/placeholder-image-square.jpg';
import {
  collateCommentsByDate,
  collateFollowersDevelopmentByDate,
  collateInteractionsByDate,
  collateInteractionsByTime,
  collatePostsByDate,
  collatePostsByTime,
  collatePostsByType,
  collatePostsByTypePerCompetitor,
  CompetitorData,
  engagementRateByFollower,
  engagementRatePerPost,
  formatCompetitorDataForComponent,
  getValueAtOrClosestDate,
  transformCompetitorData,
  TransformedData
} from './helpers';
import { LomavisComparisionTableCard1 } from '@/lomavisSharedUi/organisms/cards/LomavisComparisionTableCard1';
import { LomavisBestPerformingPosts } from '@/lomavisSharedUi/templates/cards/ILomavisBestPerformingPosts';
import { LomavisLineChart1 } from '@/lomavisSharedUi/atoms/charts/LomavisLineChart1';
import { LomavisDoughnutChartAndTable1 } from '@/lomavisSharedUi/organisms/cards/analytics/LomavisDoughnutChartAndTable1';
import { LomavisBarChart3 } from '@/lomavisSharedUi/atoms/charts/LomavisBarChart3';
import { ILomavisMultipleHeatmap1 } from '@/lomavisSharedUi/molecules/charts/ILomavisMultipleHeatmap1';
import { LomavisCard1 } from '@/lomavisSharedUi/atoms/cards/LomavisCard1';
import { LomavisPlatformIcon } from '@/lomavisSharedUi/atoms/icons/LomavisPlatformIcon';
import { LomavisUpgradeSubscriptionButton1 } from '@/lomavisSharedUi/atoms/links/LomavisUpgradeSubscriptionButton1';
import { downloadFullElementScreenshot } from '@/components/competitorAnalytics/utils/downloadFullElementScreenshot';

interface Props {
  competitors: any[];
  selectedPlatform: string;
  loadingCompetitors: boolean;
  platformInformation: any;
  totalCompetitors: number | null;
  maxCompetitors: number | null;
  dateRange: {
    startDate: any;
    endDate: any;
  };
}
const CompetitorAnalysis: React.FC<Props> = ({
  competitors,
  selectedPlatform,
  loadingCompetitors,
  platformInformation,
  totalCompetitors,
  maxCompetitors,
  dateRange
}) => {
  const { t } = useTranslate();

  const [selectedCompetitors, setSelectedCompetitors] = useState('all');
  const options = [
    { value: 'all', label: t('competitorAnalytics.allCompetitors') },
    ...competitors.map((c) => ({
      value: c.name || c.username,
      label: c.name || c.username
    }))
  ];

  const filteredCompetitors = useMemo(() => {
    if (selectedCompetitors === 'all' || !selectedCompetitors) {
      return competitors;
    }

    return competitors.filter(
      (c) =>
        selectedCompetitors === c.name || selectedCompetitors === c.username
    );
  }, [selectedCompetitors, competitors]);

  const navBarHeight =
    document.getElementById('kt_app_header')?.clientHeight + 15 * 2 + 50;
  return (
    <div className="w-100 h-100">
      {!loadingCompetitors ? (
        competitors.length > 0 ? (
          maxCompetitors && totalCompetitors > maxCompetitors ? (
            <div
              style={{
                height: `calc(100vh - ${navBarHeight}px)`
              }}
              className="w-100 d-flex  "
            >
              <div className="w-100 h-50 bg-white rounded p-2 d-flex flex-column gap-4 justify-content-center align-items-center">
                <span className="fs-3 w-50 text-center">
                  {t('lomavisCompetitorAnalysis.exceededCompetitorCount')}
                </span>
                <div className="d-flex gap-3 justify-content-center">
                  {' '}
                  <LomavisUpgradeSubscriptionButton1 />
                </div>
              </div>
            </div>
          ) : (
            <div className=" d-flex flex-column gap-2 ">
              <div className="row g-2">
                <div className="col-12 col-md-6">
                  <LomavisCard1
                    title={'lomavisCompetitorAnalysis.followers'}
                    showMoreInfo={true}
                    showSheetDownloadButton={true}
                    customDownload={(ref) =>
                      downloadFullElementScreenshot(
                        ref,
                        t('lomavisCompetitorAnalysis.followers')
                      )
                    }
                    excelData={{
                      data: competitors?.map((c, index) => ({
                        Name: c.name,
                        Username: c.username,
                        Followers: getValueAtOrClosestDate(
                          dateRange?.endDate,
                          c?.followers_development
                        )?.value
                        // you can change logic for highlight
                      }))
                    }}
                  >
                    <LomavisList1
                      placeholder={<i className="fa-regular fa-image"></i>}
                      showTooltip={true}
                      data={competitors?.map((c, index) => ({
                        image: c.profile_picture || PlaceholderImage,
                        title: c.name,
                        subtitle: c.username,
                        value: getValueAtOrClosestDate(
                          dateRange?.endDate,
                          c?.followers_development
                        )?.value,
                        highlight: (
                          Object.values(platformInformation).flat() as {
                            name?: string;
                          }[]
                        ).some((acc) => acc?.name === c.name)

                        // you can change logic for highlight
                      }))}
                    />
                  </LomavisCard1>
                </div>

                <div className="col-12 col-md-6">
                  <LomavisCard1
                    title={'lomavisCompetitorAnalysis.posts'}
                    showMoreInfo={true}
                    showSheetDownloadButton={true}
                    customDownload={(ref) =>
                      downloadFullElementScreenshot(
                        ref,
                        t('lomavisCompetitorAnalysis.posts')
                      )
                    }
                    excelData={{
                      data: competitors?.map((c, index) => ({
                        Name: c.name,
                        Username: c.username,
                        posts: c.posts_data?.length
                        // you can change logic for highlight
                      }))
                    }}
                  >
                    <LomavisList1
                      placeholder={<i className="fa-regular fa-image"></i>}
                      showTooltip={true}
                      data={competitors?.map((c, index) => ({
                        image: c.profile_picture || PlaceholderImage,
                        title: c.name,
                        subtitle: c.username,
                        value: c.posts_data?.length,
                        highlight: (
                          Object.values(platformInformation).flat() as {
                            name?: string;
                          }[]
                        ).some((acc) => acc?.name === c.name)
                      }))}
                    />
                  </LomavisCard1>
                </div>
              </div>
              <div className="row g-2">
                <div className="col-12 col-md-6">
                  <LomavisCard1
                    title={
                      'lomavisCompetitorAnalysis.engagement_rate_by_followers'
                    }
                    showMoreInfo={true}
                    showSheetDownloadButton={true}
                    customDownload={(ref) =>
                      downloadFullElementScreenshot(
                        ref,
                        t(
                          'lomavisCompetitorAnalysis.engagement_rate_by_followers'
                        )
                      )
                    }
                    excelData={{
                      data: competitors?.map((c, index) => ({
                        Name: c.name,
                        Username: c.username,
                        Engagement_rate: engagementRateByFollower(
                          c?.posts_data,
                          c?.followers_count,
                          c?.posts_data?.length
                        )
                        // you can change logic for highlight
                      }))
                    }}
                  >
                    <LomavisList1
                      placeholder={<i className="fa-regular fa-image"></i>}
                      showTooltip={true}
                      data={competitors?.map((c, index) => ({
                        image: c.profile_picture || PlaceholderImage,
                        title: c.name,
                        subtitle: c.username,
                        value: engagementRateByFollower(
                          c?.posts_data,
                          c?.followers_count,
                          c.posts_data?.length
                        ),
                        highlight: (
                          Object.values(platformInformation).flat() as {
                            name?: string;
                          }[]
                        ).some((acc) => acc?.name === c.name)
                      }))}
                    />
                  </LomavisCard1>
                </div>
                <div className="col-12 col-md-6">
                  <LomavisCard1
                    title={
                      'lomavisCompetitorAnalysis.average_interaction_per_post'
                    }
                    showMoreInfo={true}
                    showSheetDownloadButton={true}
                    customDownload={(ref) =>
                      downloadFullElementScreenshot(
                        ref,
                        t(
                          'lomavisCompetitorAnalysis.average_interaction_per_post'
                        )
                      )
                    }
                    excelData={{
                      data: competitors?.map((c, index) => ({
                        Name: c.name,
                        Username: c.username,
                        Engagement_per_post: engagementRatePerPost(
                          c?.posts_data,
                          c?.posts_data?.length
                        )
                      }))
                    }}
                  >
                    <LomavisList1
                      placeholder={<i className="fa-regular fa-image"></i>}
                      showTooltip={true}
                      data={competitors?.map((c, index) => ({
                        image: c.profile_picture || PlaceholderImage,
                        title: c.name,
                        subtitle: c.username,
                        value: engagementRatePerPost(
                          c?.posts_data,
                          c?.posts_data?.length
                        ),
                        highlight: (
                          Object.values(platformInformation).flat() as {
                            name?: string;
                          }[]
                        ).some((acc) => acc?.name === c.name)
                      }))}
                    />
                  </LomavisCard1>
                </div>
              </div>

              <div className="w-100 h-100">
                <LomavisComparisionTableCard1
                  title={t('lomavisCompetitorAnalysis.comparative_table')}
                  data={transformCompetitorData(competitors, t)
                }
                />
              </div>

              <div className={''}>
                <LomavisBestPerformingPosts
                  title={t(
                    'lomavisCompetitorAnalysis.performance_by_metric.title'
                  )}
                  itemsToShow={3}
                  data={
                    formatCompetitorDataForComponent(filteredCompetitors, t)
                      .data
                  }
                  sortOptions={
                    formatCompetitorDataForComponent(competitors, t).sortOptions
                  }
                  showProfileHead
                  customFilterOptions={options}
                  selectCustomFilter={(item) => {
                    setSelectedCompetitors(item);
                  }}
                  currentCustomFilter={selectedCompetitors}
                />
              </div>

              <div className="row g-2">
                <div className="col-12 col-md-6">
                  <LomavisCard1
                    title={'lomavisCompetitorAnalysis.chartsFollowers'}
                    showMoreInfo={false}
                  >
                    <LomavisLineChart1
                      data={collateFollowersDevelopmentByDate(competitors)}
                    />
                  </LomavisCard1>
                </div>

                <div className="col-12 col-md-6">
                  <LomavisCard1
                    title={'lomavisCompetitorAnalysis.charts_posts_per_day'}
                    showMoreInfo={false}
                  >
                    <LomavisLineChart1 data={collatePostsByDate(competitors)} />
                  </LomavisCard1>
                </div>

                <div className="col-12 col-md-6">
                  <LomavisCard1
                    title={
                      'lomavisCompetitorAnalysis.charts_interaction_per_day'
                    }
                    showMoreInfo={false}
                  >
                    <LomavisLineChart1
                      data={collateInteractionsByDate(competitors)}
                    />
                  </LomavisCard1>
                </div>
                <div className="col-12 col-md-6">
                  <LomavisCard1
                    title={'lomavisCompetitorAnalysis.charts_comments_per_day'}
                    showMoreInfo={false}
                  >
                    <LomavisLineChart1
                      data={collateCommentsByDate(competitors)}
                    />
                  </LomavisCard1>
                </div>
              </div>

              <div>
                <LomavisDoughnutChartAndTable1
                  title={t(
                    'lomavisCompetitorAnalysis.distribution_by_post_type'
                  )}
                  data={collatePostsByType(competitors).map((item) => ({
                    ...item,
                    name: t(item.name)
                  }))}
                />
              </div>
              <div>
                <LomavisCard1
                  title={'lomavisCompetitorAnalysis.competitor_post_types'}
                  showMoreInfo={false}
                  className="h-500px"
                >
                  <LomavisBarChart3
                    data={collatePostsByTypePerCompetitor(competitors)}
                    showAllXAxisLabels={true}
                  />
                </LomavisCard1>
              </div>
              <div>
                <LomavisCard1
                  title={'lomavisCompetitorAnalysis.post_time_heat_map'}
                  showMoreInfo={false}
                >
                  <ILomavisMultipleHeatmap1
                    competitorsData={collatePostsByTime(competitors, t)}
                  />
                </LomavisCard1>
              </div>
              <div>
                <LomavisCard1
                  title={'lomavisCompetitorAnalysis.interaction_time_heat_map'}
                >
                  <ILomavisMultipleHeatmap1
                    competitorsData={collateInteractionsByTime(competitors, t)}
                    metric={t('interactions')}
                  />
                </LomavisCard1>
              </div>
            </div>
          )
        ) : (
          <div className="w-100 h-100 d-flex justify-content-center align-items-center">
            {' '}
            <div
              style={{ minHeight: '500px' }}
              className="w-100 h-50 bg-white rounded p-2 d-flex flex-column gap-4 justify-content-center align-items-center"
            >
              <span className="fs-3">
                {t('lomavisCompetitorAnalysis.noCompetitor')}
              </span>
              <div className="d-flex gap-3 justify-content-center">
                {' '}
                {selectedPlatform && (
                  <LomavisPlatformIcon platform={selectedPlatform} size={40} />
                )}{' '}
              </div>
            </div>
          </div>
        )
      ) : (
        <div className="w-100 h-100 d-flex flex-column gap-2">
          <div className="row g-2">
            <div className="col-12 col-md-6">
              <div className="skeleton-loader h-250px rounded"></div>
            </div>
            <div className="col-12 col-md-6">
              <div className="skeleton-loader h-250px rounded"></div>
            </div>
            <div className="col-12 col-md-6">
              <div className="skeleton-loader h-250px rounded"></div>
            </div>
            <div className="col-12 col-md-6">
              <div className="skeleton-loader h-250px rounded"></div>
            </div>
          </div>

          <div className="w-100">
            <div className="skeleton-loader h-600px"></div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompetitorAnalysis;
